import BandeauNotifications from './notifications/BandeauNotifications';
import PanneauNotifications from './notifications/PanneauNotifications';
import { useNotifications } from '../contexts/NotificationsContext';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import TaskBell from './TaskBell';
import { BOUTON_BARRE, LIBELLE_BARRE, PASTILLE_BARRE } from './barreBoutons';
import { getContactFullLabel } from '../utils/contactIdentity';
import { formatPriceFR } from '../utils/formatFR';

const API = process.env.REACT_APP_BACKEND_URL;

// ──────────────────────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────────────────────
const formatPrice = (v) => {
  if (v === null || v === undefined || v === '') return null;
  return formatPriceFR(v, { fallback: null });
};

const buildImgSrc = (url) => {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  // Délègue au helper centralisé (gère le ?auth=TOKEN sur les routes /api/media)
  // eslint-disable-next-line global-require
  const { getMediaUrl } = require('../utils/mediaUrl');
  // lot AI — vignettes 40x40 dans le menu de recherche : jamais de `medium` (1200 px)
  return getMediaUrl(url, 'thumb');
};

// ──────────────────────────────────────────────────────────────
// Search Dropdown
// ──────────────────────────────────────────────────────────────
const SearchDropdown = ({ query, results, loading, onPick, onSeeAll }) => {
  const hasResults = (results?.properties?.length || 0) + (results?.contacts?.length || 0) > 0;

  return (
    <div
      data-testid="search-dropdown"
      className="absolute top-full left-0 right-0 mt-2 bg-white border border-slate-200 rounded-xl shadow-2xl z-50 overflow-hidden max-h-[min(70vh,480px)] overflow-y-auto"
    >
      {loading && (
        <div className="p-4 text-center text-sm text-slate-500" data-testid="search-loading">
          <i className="bi bi-arrow-repeat animate-spin mr-2"></i>Recherche…
        </div>
      )}

      {!loading && !hasResults && query.length >= 2 && (
        <div className="p-6 text-center" data-testid="search-empty">
          <i className="bi bi-search text-2xl text-slate-300"></i>
          <p className="text-sm text-slate-500 mt-2">Aucun résultat pour « {query} »</p>
        </div>
      )}

      {!loading && hasResults && (
        <div className="py-1">
          {/* Biens */}
          {(results.properties || []).length > 0 && (
            <div className="py-1">
              <div
                className="px-4 pt-2 pb-1 text-[10px] font-bold uppercase tracking-wider text-slate-400"
                data-testid="search-count-properties"
              >
                {/* lot AE3 — la route plafonne l'affichage : sans le total réel, l'utilisateur
                    croit avoir tout vu (« Nice » montrait 4 biens sur 91). */}
                Biens ({results.properties_total > results.properties.length
                  ? `${results.properties.length} affichés sur ${results.properties_total}`
                  : results.properties.length})
              </div>
              {results.properties.map((p) => (
                <button
                  key={`p-${p.id}`}
                  data-testid={`search-result-property-${p.id}`}
                  onClick={() => onPick({ type: 'property', id: p.id })}
                  className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 active:bg-slate-100 transition text-left"
                >
                  <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center shrink-0 overflow-hidden">
                    {p.photo ? (
                      <img
                        src={buildImgSrc(p.photo)}
                        alt=""
                        className="w-full h-full object-cover"
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      />
                    ) : (
                      <i className="bi bi-house text-slate-400"></i>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-800 truncate">
                      {p.titre}
                    </p>
                    <p className="text-xs text-slate-500 truncate">
                      {/* Pas de « · » orphelin quand la référence manque (brouillon nommé). */}
                      {[p.reference, p.ville, p.operation ? p.operation.replace(/_/g, ' ') : null]
                        .filter(Boolean)
                        .join(' · ')}
                    </p>
                  </div>
                  {formatPrice(p.prix) && (
                    <span className="text-xs font-semibold text-blue-600 shrink-0">
                      {formatPrice(p.prix)}
                    </span>
                  )}
                  {p.status === 'archived' && (
                    <span className="text-[9px] font-bold bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded shrink-0">
                      ARCHIVÉ
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}

          {/* Contacts */}
          {(results.contacts || []).length > 0 && (
            <div className="py-1 border-t border-slate-100">
              <div
                className="px-4 pt-2 pb-1 text-[10px] font-bold uppercase tracking-wider text-slate-400"
                data-testid="search-count-contacts"
              >
                Contacts ({results.contacts_total > results.contacts.length
                  ? `${results.contacts.length} affichés sur ${results.contacts_total}`
                  : results.contacts.length})
              </div>
              {results.contacts.map((c) => {
                const initials = `${(c.prenom || '').charAt(0)}${(c.nom || '').charAt(0)}`.toUpperCase() || '?';
                return (
                  <button
                    key={`c-${c.id}`}
                    data-testid={`search-result-contact-${c.id}`}
                    onClick={() => onPick({ type: 'contact', id: c.id })}
                    className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 active:bg-slate-100 transition text-left"
                  >
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 text-white flex items-center justify-center text-xs font-bold shrink-0 overflow-hidden">
                      {c.photo_url ? (
                        <img src={buildImgSrc(c.photo_url)} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                      ) : (
                        initials
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-800 truncate" data-testid="global-search-contact-label">
                        {getContactFullLabel(c) !== 'Sans nom' ? getContactFullLabel(c) : (c.email || '—')}
                      </p>
                      <p className="text-xs text-slate-500 truncate">
                        {[c.email, c.telephone].filter(Boolean).join(' · ')}
                      </p>
                    </div>
                    {c.statut_lead === 'chaud' && (
                      <span className="text-[9px] font-bold bg-red-100 text-red-600 px-1.5 py-0.5 rounded shrink-0">
                        <i className="bi bi-fire mr-0.5"></i>CHAUD
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {/* Voir tout — un lien par type, vers la liste filtrée sur le même terme.
              La mécanique d'URL vient du lot V : /properties?reference=… et /contacts?q=… */}
          <div className="border-t border-slate-100 p-2 space-y-1">
            {(results.properties || []).length > 0 && (
              <button
                onClick={() => onSeeAll('properties')}
                className="w-full text-center text-xs font-medium text-blue-600 hover:bg-blue-50 rounded-md py-2 transition"
                data-testid="search-see-all"
              >
                Voir {results.properties_total > results.properties.length
                  ? `les ${results.properties_total} biens`
                  : 'les biens'} pour « {query} »
                <i className="bi bi-arrow-right ml-1"></i>
              </button>
            )}
            {(results.contacts || []).length > 0 && (
              <button
                onClick={() => onSeeAll('contacts')}
                className="w-full text-center text-xs font-medium text-blue-600 hover:bg-blue-50 rounded-md py-2 transition"
                data-testid="search-see-all-contacts"
              >
                Voir {results.contacts_total > results.contacts.length
                  ? `les ${results.contacts_total} contacts`
                  : 'les contacts'} pour « {query} »
                <i className="bi bi-arrow-right ml-1"></i>
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ──────────────────────────────────────────────────────────────
// Main ModernTopBar
// ──────────────────────────────────────────────────────────────
const ModernTopBar = ({
  startNewProperty,
  startNewContact,
  showFavoritesView,
  favoriteProperties = [],
  tasksCount = 0,
  notificationsCount = 0,
  onToggleSidebar
}) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [showTabletSearch, setShowTabletSearch] = useState(false);
  // lot DJ — compteur partagé (un seul appel pour tout l'écran, cf. NotificationsContext)
  const { nonLues: notificationsNonLues } = useNotifications();
  const [panneauNotifications, setPanneauNotifications] = useState(false);
  // lot DJ2 — aperçu au survol, comme Tâches : 200 ms à l'ouverture ET à la fermeture
  const clocheRef = useRef(null);
  const minuteurOuverture = useRef(null);
  const minuteurFermeture = useRef(null);
  const clicCloche = useRef(false); // après un clic, pas de réouverture tant qu'on n'est pas sorti
  const sansSurvol = typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(hover: none)').matches;

  const entrerCloche = () => {
    if (sansSurvol || clicCloche.current) return;
    clearTimeout(minuteurFermeture.current);
    minuteurOuverture.current = setTimeout(() => setPanneauNotifications(true), 200);
  };
  const quitterCloche = () => {
    clicCloche.current = false;
    clearTimeout(minuteurOuverture.current);
    minuteurFermeture.current = setTimeout(() => setPanneauNotifications(false), 200);
  };
  const ouvrirPageNotifications = () => {
    clicCloche.current = true;
    clearTimeout(minuteurOuverture.current);
    clearTimeout(minuteurFermeture.current);
    setPanneauNotifications(false);
    navigate('/notifications');
  };
  useEffect(() => () => {
    clearTimeout(minuteurOuverture.current);
    clearTimeout(minuteurFermeture.current);
  }, []);

  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const tabletInputRef = useRef(null);
  const debounceRef = useRef(null);
  const abortRef = useRef(null);

  const favCount = favoriteProperties?.length || 0;

  // Fetch helper avec abort si nouvelle frappe
  const runSearch = useCallback(async (q) => {
    if (!q || q.length < 2) {
      setResults(null);
      setLoading(false);
      return;
    }
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API}/api/search`, {
        params: { q, limit: 8 },
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal,
      });
      setResults(data);
    } catch (e) {
      if (!axios.isCancel(e) && e.name !== 'CanceledError') {
        console.error('Search error', e);
        setResults({ properties: [], contacts: [], total: 0 });
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounce à 280 ms
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query || query.length < 2) {
      setResults(null);
      return;
    }
    debounceRef.current = setTimeout(() => runSearch(query), 280);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [query, runSearch]);

  // Fermer au clic extérieur
  useEffect(() => {
    const handler = (e) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (showTabletSearch && tabletInputRef.current) {
      tabletInputRef.current.focus();
    }
  }, [showTabletSearch]);

  const pickResult = (item) => {
    setOpen(false);
    setQuery('');
    setResults(null);
    if (item.type === 'property') {
      navigate(`/properties/${item.id}/view`);
    } else if (item.type === 'contact') {
      navigate(`/contacts/${item.id}`);
    }
  };

  const handleSeeAll = (type = 'properties') => {
    setOpen(false);
    // `reference` est la clé lue par PropertiesListPage (lot V) — `q` n'était lu par personne,
    // le lien menait donc à une liste non filtrée.
    if (type === 'contacts') {
      navigate(`/contacts?q=${encodeURIComponent(query)}`);
      return;
    }
    navigate(`/properties?reference=${encodeURIComponent(query)}`);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setOpen(false);
      return;
    }
    if (e.key === 'Enter') {
      // Si un seul résultat, aller dessus
      const props = results?.properties || [];
      const contacts = results?.contacts || [];
      if (props.length === 1 && contacts.length === 0) {
        pickResult({ type: 'property', id: props[0].id });
        return;
      }
      if (contacts.length === 1 && props.length === 0) {
        pickResult({ type: 'contact', id: contacts[0].id });
        return;
      }
      if (query.length >= 2) handleSeeAll();
    }
  };

  // Badge
  const Badge = ({ count }) => {
    if (!count) return null;
    return (
      <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center px-0.5 leading-none">
        {count}
      </span>
    );
  };

  return (
    <>
    <header data-testid="sticky-header" className="bg-white border-b border-slate-200 sticky top-0 z-40">
      <div className="flex items-center gap-3 lg:gap-6 px-4 md:px-5 lg:px-6 h-14 lg:h-16">

        {/* Hamburger + Logo (mobile only) */}
        <div className="flex items-center gap-2 md:hidden flex-shrink-0">
          <button
            data-testid="hamburger-btn"
            onClick={onToggleSidebar}
            className="flex items-center justify-center rounded-lg text-slate-600 hover:bg-slate-100 active:bg-slate-200 transition"
            style={{ minWidth: '44px', minHeight: '44px' }}
          >
            <i className="bi bi-list text-2xl"></i>
          </button>
          <img src="/icon-192.png" alt="Komerces" className="h-8 w-8" />
        </div>

        {/* Search bar - Desktop (lg+) */}
        <div className="hidden lg:block flex-1 max-w-xl relative" ref={containerRef}>
          <div className="relative">
            <i className="bi bi-search absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400"></i>
            <input
              ref={inputRef}
              data-testid="global-search-input"
              type="text"
              placeholder="Rechercher un bien, un contact, une référence..."
              value={query}
              onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
              onFocus={() => setOpen(true)}
              onKeyDown={handleKeyDown}
              className="w-full pl-10 pr-9 py-2.5 text-sm bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 placeholder:text-slate-400 transition"
            />
            {query && (
              <button
                onClick={() => { setQuery(''); setResults(null); setOpen(false); inputRef.current?.focus(); }}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-1"
                data-testid="search-clear-btn"
                title="Effacer"
              >
                <i className="bi bi-x-circle-fill text-sm"></i>
              </button>
            )}
          </div>
          {open && query.length >= 2 && (
            <SearchDropdown
              query={query}
              results={results}
              loading={loading}
              onPick={pickResult}
              onSeeAll={handleSeeAll}
            />
          )}
        </div>

        {/* Search - Tablet (md to lg) */}
        <div className="hidden md:flex lg:hidden flex-1 relative" ref={showTabletSearch ? containerRef : null}>
          {showTabletSearch ? (
            <div className="relative flex-1 max-w-md">
              <input
                ref={tabletInputRef}
                data-testid="global-search-input-tablet"
                type="text"
                placeholder="Rechercher…"
                value={query}
                onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
                onKeyDown={handleKeyDown}
                className="w-full pl-3 pr-8 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 transition"
              />
              <button
                onClick={() => { setQuery(''); setResults(null); setShowTabletSearch(false); setOpen(false); }}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400"
              >
                <i className="bi bi-x text-lg"></i>
              </button>
              {open && query.length >= 2 && (
                <SearchDropdown
                  query={query}
                  results={results}
                  loading={loading}
                  onPick={pickResult}
                  onSeeAll={handleSeeAll}
                />
              )}
            </div>
          ) : (
            <button
              data-testid="search-toggle-tablet"
              onClick={() => setShowTabletSearch(true)}
              className="flex items-center justify-center text-slate-500 hover:bg-slate-100 rounded-lg transition"
              style={{ minWidth: '44px', minHeight: '44px' }}
            >
              <i className="bi bi-search text-lg"></i>
            </button>
          )}
        </div>

        {/* Push mobile */}
        <div className="md:hidden flex-1"></div>

        {/* CTAs centraux — lot DK2 : masqués sous 768 px (bouton « + » de la barre du bas) */}
        <div className="hidden md:flex items-center gap-2 flex-shrink-0">
          <button
            data-testid="btn-new-contact"
            onClick={startNewContact}
            className="inline-flex items-center gap-2 border border-[#28A745] text-[#28A745] hover:bg-green-50 active:bg-green-100 px-3 md:px-4 py-2 rounded-lg font-medium text-sm transition-colors"
            style={{ minHeight: '44px' }}
          >
            <i className="bi bi-person-plus text-base"></i>
            <span className="hidden lg:inline">Nouveau Contact</span>
          </button>
          <button
            data-testid="btn-new-property"
            onClick={startNewProperty}
            className="inline-flex items-center gap-2 bg-[#0062CC] hover:bg-[#0056b3] active:bg-[#004A9E] text-white px-3 md:px-4 py-2 rounded-lg font-medium text-sm shadow-sm transition-colors"
            style={{ minHeight: '44px' }}
          >
            <i className="bi bi-house-add text-base"></i>
            <span className="hidden lg:inline">Nouveau Bien</span>
          </button>
        </div>

        {/* Actions utilitaires à droite — lot DJ5 : un seul cadre, icône colorée + libellé */}
        <div className="flex items-center md:pl-3 lg:pl-4 md:border-l md:border-slate-200">
          <div className="flex items-center p-0.5 rounded-[10px] border border-[#E2E8F0] bg-[#F8FAFC]">
          <TaskBell />
          <button
            data-testid="btn-favoris"
            onClick={() => showFavoritesView?.()}
            className={BOUTON_BARRE}
            title="Favoris"
            aria-label="Favoris"
          >
            <i className={`bi ${favCount > 0 ? 'bi-heart-fill' : 'bi-heart'} text-[19px] leading-none text-[#EC4899]`}></i>
            <span className={LIBELLE_BARRE}>Favoris</span>
            {favCount > 0 && (
              <span className={`${PASTILLE_BARRE} bg-red-500`}>{favCount}</span>
            )}
          </button>
          {/* Diffusion (LOT 3 iter92) — accès rapide à la vue globale des supports externes */}
          <button
            data-testid="btn-diffusion"
            onClick={() => navigate('/diffusion')}
            className={BOUTON_BARRE}
            title="Diffusion"
            aria-label="Diffusion"
          >
            <i className="bi bi-broadcast text-[19px] leading-none text-[#16A34A]"></i>
            <span className={LIBELLE_BARRE}>Diffusion</span>
          </button>
          {/* lot DJ2 — survol : aperçu après 200 ms ; clic : page Notifications */}
          <div className="relative hidden md:block" onMouseEnter={entrerCloche} onMouseLeave={quitterCloche}>
            <button
              ref={clocheRef}
              data-testid="btn-notifications"
              type="button"
              onClick={ouvrirPageNotifications}
              className={BOUTON_BARRE}
              title="Notifications — cliquez pour ouvrir la page, survolez pour aperçu"
              aria-label="Notifications"
            >
              <i className="bi bi-bell text-[19px] leading-none text-[#F59E0B]"></i>
              <span className={LIBELLE_BARRE}>Notifs</span>
              {notificationsNonLues > 0 && (
                <span data-testid="btn-notifications-badge"
                      className={`${PASTILLE_BARRE} bg-red-500`}>
                  {notificationsNonLues > 99 ? '99+' : notificationsNonLues}
                </span>
              )}
            </button>
            {panneauNotifications && (
              <PanneauNotifications
                ancreRef={clocheRef}
                onFermer={() => setPanneauNotifications(false)}
                onMouseEnter={entrerCloche}
                onMouseLeave={quitterCloche}
              />
            )}
          </div>
          {/* lot DK2 — téléphone : la même cloche, dans le cadre, sans aperçu */}
          <button
            type="button"
            onClick={() => navigate('/notifications')}
            data-testid="btn-notifications-mobile"
            aria-label="Notifications"
            className={`md:hidden ${BOUTON_BARRE}`}
          >
            <i className="bi bi-bell text-[19px] leading-none text-[#F59E0B]"></i>
            {notificationsNonLues > 0 && (
              <span className={`${PASTILLE_BARRE} bg-red-500`}>
                {notificationsNonLues > 99 ? '99+' : notificationsNonLues}
              </span>
            )}
          </button>
          </div>
        </div>
      </div>
    </header>
    {/* lot DJ — message en haut de page : sous l'en-tête, non collant */}
    <BandeauNotifications />
    </>
  );
};

export default ModernTopBar;
